import { useSearchParams } from 'react-router-dom';

import { PLOTS_FETCH_LIMIT, usePlotsForOrganization } from '@/pages/shared/hooks/usePlotsForOrganization';
import { PAGE_SIZE } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/components/plot-list/constants';

export const NUM_ITEMS_TYPE = {
  TOP_PLOTS: 'top-plots',
  ALL_PLOTS: 'all-plots',
} as const;

export type UsePlotsForItemizedGraphParams = {
  plotRenderType: (typeof NUM_ITEMS_TYPE)[keyof typeof NUM_ITEMS_TYPE];
  plotGroupId?: string;
};

export type ItemizedPlot = {
  id: string;
  name: string;
};

export const usePlotsForItemizedGraph = ({
  plotRenderType,
  plotGroupId,
}: UsePlotsForItemizedGraphParams): ItemizedPlot[] => {
  const { plotCount } = usePlotCountLimit({ plotRenderType, plotGroupId });

  const [searchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  const offset = (currentPage - 1) * plotCount;
  const plots = usePlotsForOrganization(undefined, undefined, plotCount, offset, plotGroupId);

  return (
    plots?.data?.results?.map((plot) => ({
      id: plot.id,
      name: plot.name,
    })) ?? []
  );
};

export const usePlotCountLimit = ({ plotRenderType, plotGroupId }: UsePlotsForItemizedGraphParams) => {
  const { count: totalPlotCount } = usePlotsForOrganization(undefined, undefined, 0, 0, plotGroupId).data;
  const finalPlotCount = totalPlotCount > PLOTS_FETCH_LIMIT ? PLOTS_FETCH_LIMIT : totalPlotCount;

  const plotsNumberLimit = plotRenderType === NUM_ITEMS_TYPE.TOP_PLOTS ? PAGE_SIZE : finalPlotCount;

  return {
    plotCount: plotsNumberLimit,
    isPlotCountLimited: plotsNumberLimit < totalPlotCount,
  };
};
